import React, { useEffect, useMemo } from 'react';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import moment from 'moment';
import styled from 'styled-components';

import { DatePart } from 'containers/Analytics/datePart';
import useAnalyticsContext from 'containers/Analytics/useAnalyticsContext';

// Re-exported for backward compatibility: callers historically import DatePart
// from this module. The enum now lives in a dependency-free leaf module to avoid
// a circular import (AnalyticsProvider -> DatePartSelect -> hooks -> AnalyticsProvider).
export { DatePart };

interface Props extends SelectProps {
  onChange(datepart: DatePart): void;
  value: DatePart;
  minPeriod?: DatePart;
}

const DatePartSelect = ({ onChange, value, style = {}, minPeriod = DatePart.Daily }: Props) => {
  const { dateRange } = useAnalyticsContext();

  const start = moment(dateRange[0]).startOf('day');
  const end = moment(dateRange[1]).endOf('day');

  const options = useMemo<{ value: DatePart; label: string }[]>(() => {
    const hourDiff = Math.round(end.diff(start, 'hour', true) * 10) / 10;
    const dayDiff = Math.round(end.diff(start, 'days', true) * 10) / 10;
    const weekDiff = Math.round(end.diff(start, 'weeks', true) * 10) / 10;
    const monthDiff = Math.round(end.diff(start, 'months', true) * 10) / 10;

    const value = [];
    switch (minPeriod) {
      case DatePart.Hourly:
        if (hourDiff >= 0 && hourDiff <= 60)
          value.push({ value: DatePart.Hourly, label: 'by Hour' });
        break;
      case DatePart.Daily:
        if (dayDiff >= 1 && dayDiff <= 60) value.push({ value: DatePart.Daily, label: 'by Day' });
        break;
      case DatePart.Weekly:
        if (weekDiff >= 1 && weekDiff <= 60)
          value.push({ value: DatePart.Weekly, label: 'by Week' });
        break;
      case DatePart.Monthly:
        if (monthDiff >= 1) value.push({ value: DatePart.Monthly, label: 'by Month' });
        break;
    }

    return value;
    // eslint-disable-next-line
  }, [end, start]);

  useEffect(() => {
    if (options.length && !options.find(option => option.value === value)) {
      onChange(options[0].value);
    }
  }, [options, value, onChange]);

  return <CustomSelect style={style} onChange={onChange} value={value} options={options} />;
};

export default DatePartSelect;

const CustomSelect = styled(Select)`
  .ant-select-arrow {
    top: 30%;
  }
`;

export const TitleDropdown = styled(Select)`
  font-size: 16px;

  .ant-select-selection {
    border: none;
    border-bottom: 1px dashed;

    .ant-select-selection__rendered {
      margin-right: 30px;
      margin-left: 0;
    }
  }
`;
